// Matière et brut d'une pièce, ou prix d'achat pour un composant du
// commerce. Extrait de PieceEditor pour tenir la règle des 250 lignes
// par fichier de features/ — chez AGC, un seul fichier faisait
// 1 657 lignes et mélangeait quatre domaines.
//
// ── Ce que cet écran doit rendre visible ──
//
// Une valeur matière vient de l'une de CINQ sources (§2.1), et elle est
// toujours écrasable. Sans la provenance affichée, un prix estimé par
// famille se lit exactement comme un prix d'offre fournisseur — et
// Charles n'a aucun moyen de savoir lequel des deux mérite son
// attention. C'est la pastille du §2.5 qui porte cette information.

import NumberField from '../../ui/NumberField.jsx';
import SourceDot from '../../ui/SourceDot.jsx';
import MatiereFournie from './MatiereFournie.jsx';
import NuanceRetenue from './NuanceRetenue.jsx';
import { resoudreMatiere } from '@shared/index.js';
import { formatEuroOuTiret } from '../../lib/format.js';

/**
 * Le contexte matière, monté une fois et lu par les deux blocs.
 *
 * C'est la cascade du §2.1, la même que celle du serveur. Elle vivait en
 * ligne dans `PieceMaterial` ; l'encart en a besoin aussi depuis qu'il a
 * changé de place, et deux recopies du même contexte finissent toujours
 * par diverger d'un champ.
 */
function cascadeDe(piece) {
  return resoudreMatiere({
    densite_saisie: piece.density,
    prix_kg_saisi: piece.prix_kg_retenu ?? piece.price_per_kg,
    referentiel: piece.material_ref ?? null,
    offre: piece.offre_matiere ?? null,
    famille: piece.material_famille ?? piece.material_ref?.category ?? null,
    // Sans la désignation, une matière dont la `category` est vide au
    // référentiel perd sa famille — donc sa bande de densité, donc le
    // garde-fou.
    designation: piece.material_ref?.designation ?? piece.material_label ?? null,
  });
}

/** Ce qu'on écrit à côté d'une valeur pour dire d'où elle vient. */
function Provenance({ resultat }) {
  if (!resultat?.source) return null;
  return (
    <span className="field__hint">
      <SourceDot source={resultat.source} title={resultat.note ?? undefined} />{' '}
      {resultat.note ?? ''}
    </span>
  );
}



export function PieceMaterial({
  piece, onChange, disabled, sources = {}, materiaux = [], onRecharger,
}) {
  if (piece.is_purchase) {
    return (
      <div className="grid-2">
        <NumberField
          label="Prix d'achat"
          unit="€/pc"
          value={piece.purchase_price}
          min={0}
          disabled={disabled}
          onChange={(v) => onChange({ purchase_price: v })}
        />
        <NumberField
          label="Coefficient d'achat"
          value={piece.purchase_coef}
          min={0}
          disabled={disabled}
          hint="Vide : celui des paramètres s'applique."
          onChange={(v) => onChange({ purchase_coef: v })}
        />
      </div>
    );
  }

  // La MÊME cascade que le serveur, sur le MÊME contexte : le
  // référentiel et la dernière offre voyagent avec la pièce depuis
  // `listByDevis`. Deux implémentations donneraient deux nombres sur le
  // même écran — c'est précisément ce que `shared/` existe pour empêcher.
  const cascade = cascadeDe(piece);

  const fournie = piece.matiere_fournie_client === true;

  return (
    <>
      <MatiereFournie piece={piece} disabled={disabled} onChange={onChange} />
      <div className={fournie ? 'bloc-matiere bloc-matiere--fournie' : 'bloc-matiere'} aria-disabled={fournie || undefined}>
      {/* ── DEUX LIGNES DE DEUX, ET NON TROIS CASES EN ESCALIER ──
          La grille recevait TROIS blocs : le prix au kilo restait seul sur
          sa ligne, et le bloc gagnait une rangée pour rien. Il passe à côté
          de la nuance qui le commande (16 sept. 2026). */}
      <div className="grid-2">
        <NuanceRetenue
          piece={piece} materiaux={materiaux} disabled={disabled || fournie}
          onChange={onChange} onRecharger={onRecharger} source={sources.material_label}
        />
        <div>
          <NumberField
            label="Prix au kilo"
            unit="€/kg"
            value={piece.price_per_kg}
            min={0}
            disabled={disabled || fournie}
            onChange={(v) => onChange({ price_per_kg: v })}
          />
          <Provenance resultat={cascade.prix_kg} />
        </div>
        <div>
          <NumberField
            label="Densité"
            unit="g/cm³"
            value={piece.density}
            min={0}
            disabled={disabled || fournie}
            onChange={(v) => onChange({ density: v })}
          />
          <Provenance resultat={cascade.densite} />
        </div>
      </div>

      {/* §3.7 : les cotes du BRUT vivent dans « Brut et volumes », un seul endroit. */}

      {piece.offre_matiere ? (
        <p className="field__hint">
          Dernière offre pour cette nuance :{' '}
          {formatEuroOuTiret(piece.offre_matiere.prix_kg)}/kg
          {piece.offre_matiere.fournisseur ? ` chez ${piece.offre_matiere.fournisseur}` : ''}
          {piece.offre_matiere.date_offre
            ? ` le ${new Date(piece.offre_matiere.date_offre).toLocaleDateString('fr-BE')}`
            : ''}
          .
        </p>
      ) : null}
      </div>
    </>
  );
}

export default PieceMaterial;
